import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, CreditCard, Download, FilePlus2, History, Mail, MoreHorizontal, RefreshCcw, Send, TriangleAlert, XCircle } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { PageHeader, Panel, StatusBadge, KvRow, EmptyState, Skeleton, money, shortDate, btnPrimary, btnSecondary, inputCls } from "@/components/admin/ui";
import { PAYMENT_METHODS } from "@/components/admin/billing";

const REMINDER_LABELS = { 1: "Rappel amical", 2: "2e rappel", 3: "Dernier rappel" };

function ActionsMenu({ invoice, onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const items = [
    ["pdf", Download, "Télécharger le PDF"],
    ["copy", Copy, "Copier le lien client"],
    ["duplicate", RefreshCcw, "Dupliquer"],
    ["credit-note", FilePlus2, "Créer un avoir"],
    ["cancel", XCircle, "Annuler la facture"],
  ];
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="invoice-actions-button" className={btnSecondary}>
        <MoreHorizontal className="h-4 w-4" strokeWidth={2} /> Actions
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-30 w-56 overflow-hidden rounded-xl border border-ink/[0.07] bg-white py-1 shadow-lg" data-testid="invoice-actions-menu">
          {items.map(([key, Icon, label]) => (
            <button key={key} onClick={() => { setOpen(false); onAction(key); }} data-testid={`action-${key}`}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink transition-colors hover:bg-cloud">
              <Icon className="h-4 w-4 text-slate" strokeWidth={1.5} /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentForm({ invoiceId, onDone }) {
  const [form, setForm] = useState({ amount: "", method: "bank_transfer", reference: "", note: "", date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || busy) return;
    setBusy(true);
    try {
      await adminApi.post(`/invoices/${invoiceId}/payments`, { ...form, amount: Number(form.amount) });
      toast.success("Paiement enregistré");
      onDone();
    } catch (err) {
      toast.error(apiError(err, "Paiement refusé"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="payment-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate">Montant CHF *</label>
          <input type="number" min="0.05" step="0.05" value={form.amount} required
            onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" className={`${inputCls} mt-1 font-mono`} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate">Date</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" className={`${inputCls} mt-1`} />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate">Méthode</label>
        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method" className={`${inputCls} mt-1`}>
          {Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate">Référence bancaire</label>
        <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-reference" className={`${inputCls} mt-1 font-mono`} placeholder="RF…" />
      </div>
      <button type="submit" disabled={busy} data-testid="payment-submit" className={`${btnPrimary} w-full justify-center disabled:opacity-50`}>
        {busy ? "Enregistrement…" : "Enregistrer le paiement"}
      </button>
    </form>
  );
}

export default function AdminInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [issueErrors, setIssueErrors] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get(`/invoices/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Facture introuvable"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!data) return <Skeleton rows={8} />;
  const { invoice: inv, events, credit_notes } = data;
  const isDraft = inv.status === "draft";
  const canPay = ["issued", "sent", "viewed", "partially_paid", "overdue"].includes(inv.status) && inv.balance > 0;

  const action = async (key) => {
    if (key === "pdf") { window.open(`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/invoices/${id}/pdf`, "_blank"); return; }
    if (key === "copy") {
      const link = `${window.location.origin}/facture/${inv.public_token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Lien client copié");
      return;
    }
    if (key === "duplicate") {
      const r = await adminApi.post(`/invoices/${id}/duplicate`);
      toast.success("Facture dupliquée");
      navigate(`/admin/app/invoices/${r.data.invoice.id}`);
      return;
    }
    if (key === "credit-note") {
      const mode = window.confirm("Avoir TOTAL ? (Annuler = avoir partiel)") ? "total" : "partial";
      let amount;
      if (mode === "partial") {
        amount = Number(window.prompt(`Montant de l'avoir (solde : ${money(inv.balance)})`) || 0);
        if (!amount) return;
      }
      try {
        const r = await adminApi.post(`/invoices/${id}/credit-note`, { mode, amount });
        toast.success(`Avoir ${r.data.credit_note.number} créé`);
        load();
      } catch (err) { toast.error(apiError(err, "Avoir impossible")); }
      return;
    }
    if (key === "cancel") {
      if (!window.confirm("Annuler cette facture ?")) return;
      try { await adminApi.post(`/invoices/${id}/cancel`); toast.success("Facture annulée"); load(); }
      catch (err) { toast.error(apiError(err, "Annulation impossible")); }
    }
  };

  const issue = async () => {
    setBusy(true);
    setIssueErrors(null);
    try {
      await adminApi.post(`/invoices/${id}/issue`);
      toast.success("Facture émise — numéro et référence SCOR attribués");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors) setIssueErrors(detail.errors);
      else toast.error(apiError(err, "Émission impossible"));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/invoices/${id}/send`, { origin_url: window.location.origin });
      toast.success(`Facture envoyée à ${r.data.sent_to}`);
      load();
    } catch (err) { toast.error(apiError(err, "Envoi impossible")); }
    finally { setBusy(false); }
  };

  const remind = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/invoices/${id}/reminder`, { origin_url: window.location.origin });
      toast.success(`${REMINDER_LABELS[r.data.level]} envoyé à ${r.data.sent_to}`);
      load();
    } catch (err) { toast.error(apiError(err, "Rappel impossible")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-invoice-detail">
      <Link to="/admin/app/invoices" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate transition-colors hover:text-ink">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Factures
      </Link>
      <PageHeader
        title={inv.number || "Brouillon de facture"}
        subtitle={`${inv.customer?.name}${inv.reference ? ` · Réf. ${inv.reference}` : ""}`}
        badge={<StatusBadge status={inv.status} />}
        actions={<div className="flex items-center gap-2">
          {isDraft && <Link to={`/admin/app/invoices/${id}/edit`} className={btnSecondary} data-testid="invoice-edit-link">Modifier</Link>}
          {isDraft && <button onClick={issue} disabled={busy} data-testid="invoice-issue-button" className={`${btnPrimary} disabled:opacity-50`}>
            <CreditCard className="h-4 w-4" strokeWidth={2} /> Émettre la facture</button>}
          {!isDraft && canPay && <button onClick={() => setShowPayment(!showPayment)} data-testid="invoice-add-payment" className={btnPrimary}>Enregistrer un paiement</button>}
          {!isDraft && inv.status !== "paid" && inv.status !== "credited" && inv.status !== "cancelled" && (
            <button onClick={send} disabled={busy} data-testid="invoice-send-button" className={`${btnSecondary} disabled:opacity-50`}>
              <Send className="h-4 w-4" strokeWidth={1.5} /> Envoyer</button>)}
          <ActionsMenu invoice={inv} onAction={action} />
        </div>} />

      {issueErrors && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="issue-errors">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-700">
            <TriangleAlert className="h-4 w-4" strokeWidth={2} /> Impossible de générer la QR-facture
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-red-700">
            {issueErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
          <Link to="/admin/app/settings" className={`${btnSecondary} mt-4 border-red-200 bg-white text-red-700 hover:bg-red-100`} data-testid="issue-fix-bank">
            Modifier les coordonnées bancaires</Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <Panel title="Prestation" testId="invoice-lines">
            {inv.service_info && (inv.service_info.route || inv.service_info.booking_ref) && (
              <div className="mb-4 rounded-xl bg-cloud p-4 text-sm">
                {inv.service_info.booking_ref && <p><span className="text-slate">Réservation</span> · <span className="font-mono font-medium">{inv.service_info.booking_ref}</span></p>}
                {inv.service_info.route && <p className="mt-1"><span className="text-slate">Trajet</span> · {inv.service_info.route}</p>}
                {inv.service_info.ride_date && <p className="mt-1"><span className="text-slate">Date</span> · {inv.service_info.ride_date}</p>}
              </div>
            )}
            <table className="w-full text-sm" data-testid="invoice-lines-table">
              <thead>
                <tr className="border-b border-ink/[0.07] text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-slate/60">
                  <th className="pb-2">Description</th>
                  <th className="pb-2 text-right">Qté</th>
                  <th className="pb-2 text-right">Prix unit.</th>
                  <th className="pb-2 text-right">TVA</th>
                  <th className="pb-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((l, i) => (
                  <tr key={i} className="border-b border-ink/[0.04]">
                    <td className="py-2.5 pr-2 font-medium text-ink">
                      {l.description}
                      {l.discount_value > 0 && <p className="text-xs text-emerald-600">Remise {l.discount_type === "percent" ? `−${l.discount_value} %` : `−${money(l.discount_value)}`}</p>}
                    </td>
                    <td className="py-2.5 text-right font-mono text-slate">{l.qty}</td>
                    <td className="py-2.5 text-right font-mono text-slate">{l.unit_price === 0 ? "Offert" : money(l.unit_price)}</td>
                    <td className="py-2.5 text-right font-mono text-slate">{l.vat_rate === null || l.vat_rate === undefined ? "—" : `${l.vat_rate} %`}</td>
                    <td className="py-2.5 text-right font-mono font-semibold text-ink">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="ml-auto mt-4 w-64 space-y-1.5 border-t border-ink/[0.07] pt-3 text-sm">
              <div className="flex justify-between text-slate"><span>Sous-total HT</span><span className="font-mono">{money(inv.gross ?? inv.net)}</span></div>
              {inv.discount_total > 0 && <>
                <div className="flex justify-between text-slate"><span>Remise</span><span className="font-mono">−{money(inv.discount_total)}</span></div>
                <div className="flex justify-between text-slate"><span>Net HT</span><span className="font-mono">{money(inv.net)}</span></div>
              </>}
              {inv.vat_registered
                ? <div className="flex justify-between text-slate"><span>TVA ({inv.vat_rate} %)</span><span className="font-mono">{money(inv.vat)}</span></div>
                : <p className="text-xs text-slate">TVA non applicable — entreprise non assujettie.</p>}
              <div className="flex justify-between font-semibold text-ink"><span>Total TTC</span><span className="font-mono">{money(inv.total)}</span></div>
              {inv.paid_total > 0 && <div className="flex justify-between text-emerald-600"><span>Déjà payé</span><span className="font-mono">−{money(inv.paid_total)}</span></div>}
              <div className="flex justify-between rounded-lg bg-miles px-3 py-2 font-bold text-white">
                <span>Solde à payer</span><span className="font-mono" data-testid="invoice-balance">{money(inv.balance)}</span>
              </div>
            </div>
          </Panel>

          <Panel title="Paiements" testId="invoice-payments"
            action={canPay && <button onClick={() => setShowPayment(!showPayment)} className="text-sm font-medium text-miles" data-testid="toggle-payment-form">
              {showPayment ? "Fermer" : "+ Enregistrer"}</button>}>
            {showPayment && <div className="mb-4 rounded-xl bg-cloud p-4"><PaymentForm invoiceId={id} onDone={() => { setShowPayment(false); load(); }} /></div>}
            {(inv.payments || []).length === 0 ? (
              <p className="text-sm text-slate" data-testid="no-payments">Aucun paiement enregistré.</p>
            ) : (
              <div className="space-y-2">
                {inv.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl border border-ink/[0.05] px-4 py-2.5 text-sm" data-testid={`payment-${p.id}`}>
                    <div>
                      <span className="font-mono font-semibold text-ink">{money(p.amount)}</span>
                      <span className="ml-3 text-slate">{PAYMENT_METHODS[p.method] || p.method}</span>
                      {p.reference && <span className="ml-2 font-mono text-xs text-slate/70">{p.reference}</span>}
                    </div>
                    <span className="text-xs text-slate">{shortDate(p.date)}</span>
                  </div>
                ))}
              </div>
            )}
            {(inv.reminders || []).length > 0 && (
              <p className="mt-4 text-xs text-slate" data-testid="reminders-info">
                {inv.reminders.length} rappel{inv.reminders.length > 1 ? "s" : ""} envoyé{inv.reminders.length > 1 ? "s" : ""} — dernier le {shortDate(inv.reminders[inv.reminders.length - 1].sent_at)}
              </p>
            )}
            {!isDraft && canPay && (
              <button onClick={remind} disabled={busy} data-testid="invoice-remind-button"
                className={`${btnSecondary} mt-4 disabled:opacity-50`}>
                <Mail className="h-4 w-4" strokeWidth={1.5} /> Envoyer un rappel ({(inv.reminders || []).length}/3)</button>
            )}
          </Panel>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <Panel title="Client" testId="invoice-customer">
            <p className="font-semibold text-ink">{inv.customer?.name}</p>
            {inv.customer?.email && <p className="mt-1 text-sm text-slate">{inv.customer.email}</p>}
            {inv.customer?.phone && <p className="text-sm text-slate">{inv.customer.phone}</p>}
            {(inv.customer?.street || inv.customer?.city) && (
              <p className="mt-1 text-sm text-slate">{[inv.customer.street, inv.customer.house_num].filter(Boolean).join(" ")}<br />
                {[inv.customer.pcode, inv.customer.city].filter(Boolean).join(" ")} {inv.customer.country}</p>)}
            {inv.billing?.po_number && <KvRow label="Réf. client / PO" mono>{inv.billing.po_number}</KvRow>}
            {inv.billing?.billing_email && <KvRow label="E-mail facturation">{inv.billing.billing_email}</KvRow>}
            {inv.customer_id && <Link to={`/admin/app/customers/${inv.customer_id}`} className="mt-2 inline-block text-sm font-medium text-miles" data-testid="invoice-customer-link">Voir la fiche client →</Link>}
          </Panel>

          <Panel title="Règlement" testId="invoice-terms">
            <KvRow label="Échéance">{shortDate(inv.due_date) || "À l'émission"}</KvRow>
            {inv.reference && <KvRow label="Référence" mono>{inv.reference_type === "SCOR" ? inv.reference.replace(/(.{4})/g, "$1 ").trim() : inv.reference}</KvRow>}
            {inv.from_quote && <KvRow label="Devis d'origine" mono>{inv.from_quote}</KvRow>}
            {inv.terms && <p className="mt-2 text-xs text-slate">{inv.terms}</p>}
            {inv.notes && <p className="mt-1 text-xs text-slate">{inv.notes}</p>}
            {(credit_notes || []).length > 0 && (
              <div className="mt-3 border-t border-ink/[0.06] pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate/60">Avoirs liés</p>
                {credit_notes.map((cn) => (
                  <a key={cn.id} href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/credit-notes/${cn.id}/pdf`} target="_blank" rel="noreferrer"
                    className="mt-1.5 flex items-center justify-between text-sm" data-testid={`credit-note-${cn.number}`}>
                    <span className="font-mono font-medium text-ink">{cn.number}</span>
                    <span className="font-mono text-red-600">−{money(cn.amount)}</span>
                  </a>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={<span className="flex items-center gap-1.5"><History className="h-4 w-4" strokeWidth={1.5} /> Historique</span>} testId="invoice-history">
            {(events || []).length === 0 ? <EmptyState title="Aucun événement" testId="no-events" /> : (
              <div className="space-y-3">
                {events.map((e) => (
                  <div key={e.id} className="flex gap-3 text-sm">
                    <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-miles" />
                    <div>
                      <p className="text-ink">{e.details || e.event}</p>
                      <p className="text-xs text-slate">{shortDate(e.at)} · {e.actor}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
